/**
 * Lift2Fit — the lifter's coach, beside the lifter's chat with that coach.
 *
 * The coach's side of this workspace carries "Their training": the programme and
 * progress of the client being talked to. The lifter's side had nothing, which
 * made a conversation with a coach a conversation with a name. This is the same
 * idea pointing the other way — who the coach is, what they sell, what deal is
 * live, and the review the lifter left (or is about to).
 *
 * The review form lives here on purpose. Asking for a review is useless if it
 * only exists on a profile page the lifter has to remember: it belongs at the
 * moment they are talking to the coach, in the same panel as the coach's rating,
 * so "4.0 / 5 from 3 clients" is immediately followed by "what would you say?".
 *
 * One review per lifter per coach (the API is a PUT, so a second save edits).
 * The same component answers `#/chat?side=lifter`, where it is the only rail a
 * lifter has.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type CoachPack,
  type LifterCoachCard,
  type Problem,
} from '@/lib/api';
import { Button } from '@/components/ui';
import { Panel, StatusChip } from '../components';
import { formatMoney } from '../crm/shared';
import { useAsync } from '../hooks';

const STARS = [1, 2, 3, 4, 5] as const;

function ratingLine(rating: LifterCoachCard['rating']): string {
  if (rating === null || rating.count === 0) return 'No reviews yet';
  const average = (Math.round(rating.average * 100) / 100).toFixed(1);
  return `${average} / 5 · ${rating.count} review${rating.count === 1 ? '' : 's'}`;
}

export function LifterCoachCard({
  coachId,
  testId = 'chat-coach-card',
}: {
  coachId: string;
  testId?: string;
}) {
  const card = useAsync(() => api.myCoachCard(), [coachId]);
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  const data = card.data;
  const mine = data?.myReview ?? null;

  // Prefill from the review already written, so the form edits rather than
  // pretending the lifter has never rated this coach.
  useEffect(() => {
    if (mine === null) return;
    setRating(mine.rating);
    setNote(mine.comment ?? '');
  }, [mine?.reviewId, mine?.rating, mine?.comment]);

  const save = async () => {
    if (rating < 1) {
      setProblem({ type: 'validation', status: 422, title: 'Pick a rating', detail: 'Choose one to five stars first.' });
      return;
    }
    setBusy(true);
    setProblem(null);
    setMessage(null);
    const result = await api.saveReview(coachId, { rating, comment: note.trim() === '' ? null : note.trim() });
    setBusy(false);
    if (result.problem || !result.data) {
      setProblem(
        result.problem ?? { type: 'transport', status: 0, title: 'Not saved', detail: 'The review was not saved.' },
      );
      return;
    }
    setMessage(
      mine === null
        ? `Thanks — your ${rating}-star review is published on ${data?.displayName ?? 'your coach'}.`
        : `Updated to ${rating} stars.`,
    );
    card.reload();
  };

  if (card.loading && !data) {
    return <p className="text-[12px] text-ink-muted">Loading your coach…</p>;
  }

  if (!data) {
    return (
      <div data-testid={testId} className="flex flex-col gap-2">
        <p className="text-[12px] text-ink-muted">
          {card.error ?? 'You are not connected to a coach yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[15px] font-bold text-ink" data-testid="chat-coach-name">
            {data.displayName}
          </p>
          <StatusChip tone="accent">Your coach</StatusChip>
        </div>
        <p className="flex items-center gap-1.5 text-[12px] text-ink-muted" data-testid="chat-coach-rating">
          <span aria-hidden="true" className="text-accent">
            ★
          </span>
          {ratingLine(data.rating)}
        </p>
        {data.location === null ? null : (
          <p className="text-[12px] text-ink-muted" data-testid="chat-coach-location">
            {data.location}
          </p>
        )}
        {data.specialities.length === 0 ? null : (
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted">
            {data.specialities.join(' · ')}
          </p>
        )}
      </div>

      {data.packs.length === 0 ? null : (
        <div className="flex flex-col gap-1 border-t border-hairline pt-2" data-testid="chat-coach-packs">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">Packs</p>
          {data.packs.slice(0, 3).map((pack: CoachPack) => (
            <p key={pack.packId} className="text-[12px] text-ink">
              <span className="font-bold">{pack.name}</span>{' '}
              <span className="text-ink-muted">
                {formatMoney(pack.price, pack.currency)} · {pack.cadence.replace(/_/g, ' ')}
              </span>
            </p>
          ))}
        </div>
      )}

      {data.offers.length === 0 ? null : (
        <div className="flex flex-col gap-1 border-t border-hairline pt-2" data-testid="chat-coach-offers">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">On offer now</p>
          {data.offers.map((offer) => (
            <p key={offer.offerId} className="text-[12px] text-ink">
              <span className="font-bold">{offer.name}</span>{' '}
              <span className="text-ink-muted">{offer.summary}</span>
              {offer.code === null ? null : (
                <>
                  {' · code '}
                  <span className="font-mono font-bold tracking-[0.06em]">{offer.code}</span>
                </>
              )}
            </p>
          ))}
        </div>
      )}

      <Panel
        testId="chat-coach-review"
        title={mine === null ? 'Rate your coach' : 'Your review'}
        subtitle={
          mine === null
            ? 'One review per coach. It appears on their public profile when they publish reviews.'
            : 'You can edit it any time — it stays one review.'
        }
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1" role="group" aria-label="Rating out of five">
            {STARS.map((value) => (
              <button
                key={value}
                type="button"
                aria-label={`${value} star${value === 1 ? '' : 's'}`}
                aria-pressed={rating === value}
                data-testid={`chat-coach-star-${value}`}
                onClick={() => setRating(value)}
                className={`text-[20px] leading-none transition-colors ${
                  value <= rating ? 'text-accent' : 'text-ink-muted hover:text-accent'
                }`}
              >
                ★
              </button>
            ))}
            <span className="ml-1 text-[12px] text-ink-muted" data-testid="chat-coach-rating-chosen">
              {rating === 0 ? 'not rated yet' : `${rating} / 5`}
            </span>
          </div>
          <textarea
            rows={2}
            maxLength={2000}
            data-testid="chat-coach-review-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="What is it like training with them? (optional)"
            className="rounded-chat border border-hairline bg-page px-3 py-2 text-[13px] text-ink"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              data-testid="chat-coach-review-save"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : mine === null ? 'Publish review' : 'Update review'}
            </Button>
            {message === null ? null : (
              <span className="text-[12px] text-ink" data-testid="chat-coach-review-message">
                {message}
              </span>
            )}
          </div>
          {problem === null ? null : (
            <p className="text-[12px] text-danger" data-testid="chat-coach-review-problem">
              {problem.detail ?? problem.title}
            </p>
          )}
        </div>
      </Panel>
    </div>
  );
}
